import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SequenceImage } from '../../StitchingTool/Stitching/Stitching.reducer';
import { AppThunk } from '../../../store';
import axios from '../../../axios';
import { AnnotationStandardDetails, resetState as resetFromSlice } from '../AnnotationStandardForm/AnnotationStandardForm.slice';
import { resetState as resetCanvas, annotationCreationCanceled, imageLoading, imageLoaded } from '../Canvas/canvas.slice';
import { resetState as resetStitchedView } from '../StitchedView/stitchedView.slice';
import { setShowCreateAnnotationModal, setInitialDistanceToRoot, setSelectedAnnotationForEdit } from './AnnotationForm/AnnotationForm.slice';
import { ValidationModalDto, setValidationData } from './AnnotationValidationModal/AnnotationValidationModal.slice';
import {
  getImageNameWithoutExtensions,
  sortImagesAscByTopFunction,
  populateSequenceImageInfoWithRawData,
  TABLE_STATUS
} from '../../../shared/Helpers';
import { getAnnotationSettings, shouldShowImageTreeStructure } from './AnnotationSettings';
import { ToastAnt } from '../../Shared/Toast/ToastAnt';
import { Modal } from 'antd';
import { isStringNullOrEmpty } from './AnnotationForm/Utils';
import { DataQCAssessment, getTurbineDataQCAssessment } from './Tree/QC/qc.slice';
import { getCurrentAndPossibleTurbineStatuses } from './Tree/TurbineStatus/turbineStatus.slice';
import { PublishRequest } from '../../Shared/Landing/Landing.slice';
import {
  AnnotationSettings,
  AnnotationsForGrid,
  CachedImage,
  CachedSequence,
  ImageRawData,
  ImagesAndSequenceInfo,
  ImageSettings,
  IndexRange,
  Localization,
  MLRequest,
  PathContextType,
  SaveImageSettings,
  SequenceInfo,
  SequenceResponse,
  SequenceStatusInfo,
  TurbineSequencesStatus
} from './annotation.types';

const PreloadCount = 1;

type State = {
  fullScreenMode: boolean;
  annotationsGrid: AnnotationsForGrid[] | null;
  annotationStandardDetails: AnnotationStandardDetails | null;
  canAnnotate: boolean;
  tableStatus: number;
  havePermissionToAnnotate: boolean;
  canPublish: boolean;
  isPublishing: boolean;
  redirectToRoot: boolean;
  callMLServices: boolean;
  sequenceStatuses: Array<SequenceStatusInfo>;
  selectedAnnotationId: string;
  currentPath: string;
  pathContext: PathContextType;
  lastCalledPath: string;

  //let see
  images: SequenceImage[] | null;
  subsampleFactor: number;

  //sequence
  selectedSequenceInfo: SequenceInfo | null;
  sequencesCache: CachedSequence[];
  imagesCache: CachedImage[];
  sequencePath: string;

  //only tree
  isLoadingImages: boolean;

  //annotations
  allHiddenColumns: string[];
  isGettingAnnotations: boolean;

  //undo-redo
  pastAnnotations: AnnotationsForGrid[][];
  presentAnnotations: AnnotationsForGrid[] | null;
  futureAnnotations: AnnotationsForGrid[][];

  //image
  isPhotocardSelected: boolean;
  image: ImageRawData | null;
  selectedName: string;

  //annotation
  findingLocalizationDto: Localization | null;
  annotationSetForUpdate: boolean;
  localizationLoading: boolean;
  localizationEditable: boolean;

  findingChangedOnArrow: boolean;

  imageSettings: ImageSettings | null;
  showImageTree: boolean;

  //table filter
  appliedFilters: Record<string, React.Key[] | null> | null;

  //canvas zoom and position
  viewportTransform: number[] | undefined;
};

let initialState: State = {
  images: null,
  subsampleFactor: 0,
  selectedSequenceInfo: null,
  sequencesCache: [],
  imagesCache: [],
  redirectToRoot: false,
  fullScreenMode: false,
  image: null,
  selectedName: '',
  annotationsGrid: null,
  isGettingAnnotations: false,
  annotationStandardDetails: null,
  sequencePath: '',
  findingLocalizationDto: null,
  sequenceStatuses: [],
  isLoadingImages: false,
  canAnnotate: false,
  havePermissionToAnnotate: false,
  canPublish: false,
  callMLServices: false,
  localizationEditable: false,
  selectedAnnotationId: '',
  annotationSetForUpdate: false,
  isPublishing: false,
  tableStatus: TABLE_STATUS.NORMAL,
  pastAnnotations: [],
  presentAnnotations: null,
  futureAnnotations: [],
  allHiddenColumns: [],
  localizationLoading: false,
  isPhotocardSelected: false,
  currentPath: '',
  pathContext: 'no-images',
  lastCalledPath: '',
  findingChangedOnArrow: false,
  imageSettings: null,
  showImageTree: false,
  appliedFilters: null,
  viewportTransform: undefined
};

type UpdatedTurbineQCUncheckData = {
  updatedSelectedSequenceInfo?: SequenceInfo;
  updatedImagesCache?: CachedImage[];
  updatedSequenceStatuses?: SequenceStatusInfo[];
};

type UpdatedImageQCData = {
  assessmentData: DataQCAssessment;
} & UpdatedTurbineQCUncheckData;

const annotationSlice = createSlice({
  name: 'annotation',
  initialState,
  reducers: {
    resetState() {
      return initialState;
    },
    setImagesAndCache(state, action: PayloadAction<ImagesAndSequenceInfo>) {
      const { images, newCache, info, subsampleFactor } = action.payload;
      const { sequencesCache } = state;

      state.images = images;
      state.subsampleFactor = subsampleFactor;
      sequencesCache.push({ sequence: newCache, info });
    },
    onSelectSequence(state, action: PayloadAction<string>) {
      state.sequencePath = action.payload;
    },
    onNodeChange(state) {
      state.image = null;
      state.selectedName = '';
    },
    setSequenceFromCache(state, action: PayloadAction<string>) {
      const cacheId = action.payload;
      const { sequencesCache } = state;
      const cachedSequence = sequencesCache.find(x => x.sequence && x.sequence.id === cacheId);
      const { sequence, info } = cachedSequence!;

      state.images = sequence.images;
      state.subsampleFactor = sequence.subsampleFactor;
      state.selectedSequenceInfo = info;
      state.showImageTree = shouldShowImageTreeStructure(info);
    },
    setPathContext(state, action: PayloadAction<PathContextType>) {
      state.pathContext = action.payload;
    },
    redirectToRoot(state) {
      state.redirectToRoot = true;
    },
    fullScreenModeChanged(state, acttion: PayloadAction<boolean>) {
      state.fullScreenMode = acttion.payload;
    },
    setImage(state, action: PayloadAction<ImageRawData>) {
      state.image = action.payload;
    },
    setSelectedName(state, action: PayloadAction<string>) {
      state.selectedName = action.payload;
    },
    setImageSetings(state, action: PayloadAction<ImageSettings>) {
      state.imageSettings = action.payload;
    },
    setGrid(state, action: PayloadAction<AnnotationsForGrid[]>) {
      state.annotationsGrid = action.payload;
    },
    setAnnotationStandard(state, action: PayloadAction<AnnotationStandardDetails>) {
      state.annotationStandardDetails = action.payload;
    },
    setLocalizationDto(state, action: PayloadAction<any>) {
      state.findingLocalizationDto = action.payload;
    },
    setSequenceStatuses(state, action: PayloadAction<Array<SequenceStatusInfo>>) {
      state.sequenceStatuses = action.payload;
    },
    setSelectedSequenceInfos(state, action: PayloadAction<SequenceInfo>) {
      state.selectedSequenceInfo = action.payload;
      state.showImageTree = shouldShowImageTreeStructure(action.payload);

      state.sequencesCache = state.sequencesCache.map(x => {
        if (x.info.id === action.payload.id) {
          return {
            sequence: x.sequence,
            info: action.payload
          };
        }

        return x;
      });

      state.sequenceStatuses = state.sequenceStatuses.map(x => {
        if (x.blade === action.payload.bladePosition && x.bladeSide === action.payload.bladeSide) {
          return {
            ...x,
            annotationsCount: action.payload.images.map(x => x.annotationsCount).reduce((a, b) => a + b, 0),
            annotationsToBeTransferredCount: action.payload.images.map(x => x.annotationsToBeTransferredCount).reduce((a, b) => a + b, 0)
          };
        }

        return x;
      });
    },
    clearSelectedSequenceInfos(state) {
      state.selectedSequenceInfo = null;
    },
    setImagesLoading(state) {
      state.isLoadingImages = true;
    },
    setImagesLoaded(state) {
      state.isLoadingImages = false;
    },
    setAnnotationSettings(state, action: PayloadAction<AnnotationSettings>) {
      const { callMLServices, canAnnotate, localizationEditable } = action.payload;

      state.callMLServices = callMLServices;
      state.canAnnotate = canAnnotate;
      state.localizationEditable = localizationEditable;
    },
    setSelectedAnnotationId(state, action: PayloadAction<string>) {
      state.selectedAnnotationId = action.payload;
    },
    setAnnotationSetForUpdate(state, action: PayloadAction<boolean>) {
      state.annotationSetForUpdate = action.payload;
    },
    setIsPublishing(state, action: PayloadAction<boolean>) {
      state.isPublishing = action.payload;
    },
    setStatusTable(state, action: PayloadAction<number>) {
      state.tableStatus = action.payload;
    },
    cacheNewImage(state, action: PayloadAction<CachedImage>) {
      state.imagesCache.push(action.payload);
    },
    clearCachedImages(state, action: PayloadAction<IndexRange>) {
      const { selectedSequenceInfo, sequencePath } = state;
      const { from, to } = action.payload;
      if (selectedSequenceInfo) {
        const images = selectedSequenceInfo.images.filter(x => !x.isHidden);
        for (let index = 0; index < images.length; index++) {
          if (index < from || index > to) {
            const image = images[index];
            const url = getImageUrl(sequencePath, image.filename);
            state.imagesCache = state.imagesCache.filter(x => x.url !== url);
          }
        }
      }
    },
    clearAllCachedImages(state) {
      state.imagesCache = [];
    },
    undoFinished(state) {
      const last = state.pastAnnotations.pop();
      state.futureAnnotations.unshift([...state.presentAnnotations!]);
      state.presentAnnotations = last!;
    },
    redoFinished(state) {
      const first = state.futureAnnotations.shift();
      state.pastAnnotations.push([...state.presentAnnotations!]);
      state.presentAnnotations = first!;
    },
    updatedHistory(state, action: PayloadAction<AnnotationsForGrid[]>) {
      if (state.presentAnnotations) {
        state.pastAnnotations.push([...state.presentAnnotations]);
      }
      if (!action.payload) {
        state.pastAnnotations.push([]);
      }
      state.presentAnnotations = action.payload;
      state.futureAnnotations = [];
    },
    clearHistory(state) {
      state.pastAnnotations = [];
      state.presentAnnotations = null;
      state.futureAnnotations = [];
      state.findingLocalizationDto = null;
      state.annotationSetForUpdate = false;
    },
    updatedPresentAnnotations(state, action: PayloadAction<AnnotationsForGrid[]>) {
      state.presentAnnotations = action.payload;
    },
    setAllHiddenColumns(state, action: PayloadAction<string[]>) {
      state.allHiddenColumns = action.payload;
    },
    setLocalizationLoading(state, action: PayloadAction<boolean>) {
      state.localizationLoading = action.payload;
    },
    setHavePermissionToAnnotate(state, action: PayloadAction<{ canAnnotate: boolean; canPublish: boolean }>) {
      state.havePermissionToAnnotate = action.payload.canAnnotate;
      state.canPublish = action.payload.canPublish;
    },
    setDataAfterImageQCAssessment(state, action: PayloadAction<UpdatedImageQCData>) {
      const { assessmentData, updatedSelectedSequenceInfo, updatedImagesCache, updatedSequenceStatuses } = action.payload;

      if (state.image) {
        state.image.dataQCAssessment = assessmentData;
      }

      if (updatedSelectedSequenceInfo && updatedImagesCache && updatedSequenceStatuses) {
        state.selectedSequenceInfo = updatedSelectedSequenceInfo;
        state.imagesCache = updatedImagesCache;
        state.sequenceStatuses = updatedSequenceStatuses;
      }
    },
    setDataAfterTurbineQCUncheckAssessment(state, action: PayloadAction<UpdatedTurbineQCUncheckData>) {
      const { updatedSelectedSequenceInfo, updatedImagesCache, updatedSequenceStatuses } = action.payload;

      if (updatedSelectedSequenceInfo && updatedImagesCache && updatedSequenceStatuses) {
        state.selectedSequenceInfo = updatedSelectedSequenceInfo;
        state.imagesCache = updatedImagesCache;
        state.sequenceStatuses = updatedSequenceStatuses;
      }
    },
    setIsPhotocardSelected(state, action: PayloadAction<boolean>) {
      state.isPhotocardSelected = action.payload;
    },
    setCurrentPath(state, action: PayloadAction<string>) {
      state.currentPath = action.payload;
    },
    setIsGettingAnnotations(state, action: PayloadAction<boolean>) {
      state.isGettingAnnotations = action.payload;
    },
    setLastCalledPath(state, action: PayloadAction<string>) {
      state.lastCalledPath = action.payload;
    },
    setFindingChangedOnArrow(state, action: PayloadAction<boolean>) {
      state.findingChangedOnArrow = action.payload;
    },
    setAppliedFilters(state, action: PayloadAction<Record<string, React.Key[] | null> | null>) {
      state.appliedFilters = action.payload;
    },
    setViewportTransform(state, action: PayloadAction<number[] | undefined>) {
      state.viewportTransform = action.payload;
    }
  }
});

export const {
  resetState,
  setImagesAndCache,
  onSelectSequence,
  onNodeChange,
  setSequenceFromCache,
  redirectToRoot,
  fullScreenModeChanged,
  setImage,
  setSelectedName,
  setImageSetings,
  setGrid,
  setAnnotationStandard,
  setLocalizationDto,
  setSequenceStatuses,
  setSelectedSequenceInfos,
  clearSelectedSequenceInfos,
  setImagesLoading,
  setImagesLoaded,
  setAnnotationSettings,
  setSelectedAnnotationId,
  setAnnotationSetForUpdate,
  setIsPublishing,
  cacheNewImage,
  clearCachedImages,
  clearAllCachedImages,
  undoFinished,
  redoFinished,
  updatedHistory,
  clearHistory,
  setAllHiddenColumns,
  setLocalizationLoading,
  setHavePermissionToAnnotate,
  setStatusTable,
  setDataAfterImageQCAssessment,
  setDataAfterTurbineQCUncheckAssessment,

  updatedPresentAnnotations,
  setIsPhotocardSelected,
  setCurrentPath,
  setPathContext,
  setIsGettingAnnotations,
  setLastCalledPath,
  setFindingChangedOnArrow,
  setAppliedFilters,
  setViewportTransform
} = annotationSlice.actions;

export default annotationSlice.reducer;

export const resetAppliedFilters = (): AppThunk => async (dispatch, getState) => {
  dispatch(setAppliedFilters(null));
};

export const checkTurbineInfo =
  (turbinePath: string, refreshNodeData?: boolean, containsSequence?: boolean): AppThunk =>
  async (dispatch, getState) => {
    const { selectedName } = getState().annotation;
    if (turbinePath.length) {
      const response = await axios.get<TurbineSequencesStatus>(`sequence/${turbinePath}`);
      const { exist, message, canAnnotate, sequences, annotationStandardId, canPublish } = response.data;

      if (!exist) {
        ToastAnt(message);
        dispatch(redirectToRoot());
        return;
      }

      if (message) {
        ToastAnt(message);
      }

      dispatch(setHavePermissionToAnnotate({ canAnnotate, canPublish }));
      dispatch(setSequenceStatuses(sequences));
      dispatch(getCurrentAndPossibleTurbineStatuses(turbinePath));

      if (refreshNodeData) {
        dispatch(getAnnotationStandardDetails(annotationStandardId));

        if (!containsSequence) {
          dispatch(prepareNodeData(turbinePath));
        }
      }
    }
  };

export const prepareNodeData =
  (nodePath: string, imageName?: string, updateHistory?: boolean): AppThunk =>
  async (dispatch, getState) => {
    try {
      const { lastCalledPath, presentAnnotations, pathContext } = getState().annotation;
      dispatch(setIsGettingAnnotations(true));

      dispatch(setLastCalledPath(nodePath));
      const response = await axios.get<AnnotationsForGrid[]>(
        `annotations/getAll/${nodePath}?imageName=${imageName ? imageName : ''}&sortAnnotations=${pathContext === 'with-images'}`
      );
      dispatch(setGrid(response.data));

      if (lastCalledPath !== nodePath) {
        dispatch(clearHistory());
      }

      if (updateHistory || !presentAnnotations) {
        dispatch(updatedHistory(response.data));
      }
    } catch (error) {
      console.log(error);
    }

    dispatch(setIsGettingAnnotations(false));
  };

export const prepareSequence =
  (newSequencePath: string): AppThunk =>
  async (dispatch, getState) => {
    const { sequencesCache, sequencePath, pathContext, selectedName, lastCalledPath } = getState().annotation;

    if (sequencePath === newSequencePath) {
      if (pathContext === 'with-images' && !selectedName) {
        dispatch(setGrid([]));
      }
      return;
    }

    const cacheSequence = sequencesCache.find(x => x.sequence && x.sequence.id === newSequencePath);

    dispatch(clearAllCachedImages());
    dispatch(onSelectSequence(newSequencePath));
    dispatch(setSelectedName(''));
    if (pathContext === 'with-images') {
      dispatch(setGrid([]));
      dispatch(clearHistory());
    }

    if (cacheSequence) {
      dispatch(setSequenceFromCache(newSequencePath));
      dispatch(getSequenceAnnotations(true));
      return;
    }

    const sortAnnotations = pathContext === 'with-images';

    const imagesUrl = `sequence/${newSequencePath}/${sortAnnotations}`;

    try {
      const response = await axios.get<SequenceResponse>(imagesUrl);

      const { stitchedViewData, sequence } = response.data;

      if (stitchedViewData) {
        const { images } = stitchedViewData;

        sequence.hasStitchedView = true;

        populateSequenceImageInfoWithRawData(sequence, images);
        populateRankIfNotSet(sequence, images);

        dispatch(
          setImagesAndCache({
            images,
            subsampleFactor: sequence.subsampleFactor,
            newCache: {
              id: newSequencePath,
              isStitched: true,
              images,
              subsampleFactor: sequence.subsampleFactor,
              isRejected: false,
              stitchingManipulationRules: {
                lockMovementX: true,
                lockMovementY: true,
                lockScalingX: true,
                lockScalingY: true,
                lockRotation: true
              }
            },
            info: sequence
          })
        );
      } else {
        dispatch(setImagesLoaded());
      }

      dispatch(setSelectedSequenceInfos(sequence));
    } catch (error) {
      console.log(error);
    }
  };

export const getSequenceAnnotations =
  (updateGrid = false, updateHistory = false): AppThunk =>
  async (dispatch, getState) => {
    const { sequencePath, isPhotocardSelected, images, pathContext, lastCalledPath } = getState().annotation;

    if (isStringNullOrEmpty(sequencePath)) {
      dispatch(prepareNodeData(lastCalledPath, undefined, updateHistory));
      return;
    }

    const sortAnnotations = pathContext === 'with-images';

    const annotationsUrl = `sequence/annotations/${sequencePath}/${sortAnnotations}`;

    const response = await axios.get<SequenceInfo>(annotationsUrl);

    const sequence = response.data;

    if (images) {
      sequence.hasStitchedView = true;
      populateSequenceImageInfoWithRawData(sequence, images);
    }

    dispatch(setSelectedSequenceInfos(sequence));

    if (updateGrid) {
      if (pathContext === 'no-images') {
        dispatch(prepareNodeData(lastCalledPath, undefined, updateHistory));
      } else {
        dispatch(getImageAnnotations(isPhotocardSelected, updateHistory));
      }
    }
  };

export const NO_FILTERS_APPLIED_SETTINGS = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  applyToAll: false
};

export const imageSelected =
  (url: string, newName: string): AppThunk =>
  async (dispatch, getState) => {
    const { imagesCache } = getState().annotation;

    dispatch(clearHistory());

    dispatch(imageLoading());
    const cached = imagesCache.find(x => x.url === url);
    dispatch(setSelectedName(newName));
    dispatch(setImageSetings(NO_FILTERS_APPLIED_SETTINGS));
    if (cached) {
      setTimeout(() => {
        dispatch(setImage(cached.image));
        dispatch(setViewportTransform(undefined));
      }, 10);
      return;
    }

    try {
      const { data: image } = await axios.get<ImageRawData>(url);
      dispatch(setImage(image));
      dispatch(setViewportTransform(undefined));
      dispatch(cacheNewImage({ url, image }));
    } catch (error) {
      dispatch(imageLoaded());
    }
  };

export const preloadImages =
  (newName: string): AppThunk =>
  async (dispatch, getState) => {
    const { selectedSequenceInfo } = getState().annotation;

    if (!selectedSequenceInfo) {
      return;
    }

    const selectedImageInfo = selectedSequenceInfo.images.find(
      item => getImageNameWithoutExtensions(item.filename) === getImageNameWithoutExtensions(newName)
    );
    const currentIndex = selectedSequenceInfo.images.indexOf(selectedImageInfo!);
    const totalCount = selectedSequenceInfo.images.filter(x => !x.isHidden).length;
    let prevPreload = PreloadCount,
      nextPreload = PreloadCount;

    if (currentIndex + nextPreload > totalCount - 1) {
      nextPreload = totalCount - currentIndex - 1;
    }

    if (currentIndex - prevPreload < 0) {
      prevPreload = currentIndex;
    }

    // dispatch(clearCachedImages({ from: currentIndex - prevPreload, to: currentIndex + nextPreload }));
    dispatch(loadImagesFromToIndex(currentIndex - prevPreload, currentIndex - 1));
    dispatch(loadImagesFromToIndex(currentIndex + 1, currentIndex + nextPreload));
  };

const loadImagesFromToIndex =
  (from: number, to: number): AppThunk =>
  async (dispatch, getState) => {
    const { selectedSequenceInfo, sequencePath, imagesCache } = getState().annotation;

    if (!selectedSequenceInfo) {
      return;
    }

    for (let index = from; index <= to; index++) {
      const image = selectedSequenceInfo.images.filter(x => !x.isHidden)[index];
      const url = getImageUrl(sequencePath, image.filename);
      if (imagesCache.find(x => x.url === url)) continue;
      dispatch(loadImageToCache(url));
    }
  };

const loadImageToCache =
  (url: string): AppThunk =>
  async dispatch => {
    const { data: image } = await axios.get<ImageRawData>(url);
    preloadImage(image.src);
    dispatch(cacheNewImage({ url, image }));
  };

const getImageUrl = (sequencePath: string, filename: string) => `sequence/image/${sequencePath}/${filename}`;

const preloadImage = (src: string) => {
  var img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = src;
};

export const getFindingLocalizationDto =
  (mlRequest: MLRequest, validationDto: ValidationModalDto, isUpdate: boolean = false): AppThunk =>
  async (dispatch, getState) => {
    const { callMLServices, presentAnnotations } = getState().annotation;

    const checkForDirtyFinding = () => {
      const annotation = presentAnnotations?.find(x => x.dirty);
      if (annotation) {
        dispatch(clearDirtyFlagOnFinding(annotation.id));
      }
    };

    const outsideOfImage = () => {
      checkForDirtyFinding();

      validationDto.showModal = false;
      dispatch(annotationCreationCanceled(true));
      dispatch(setSelectedAnnotationForEdit(null));
      ToastAnt('Outside of image. Reverted.');
    };

    if (!callMLServices) {
      dispatch(setLocalizationDto({}));
      dispatch(setValidationData({}));

      if (validationDto.outOfBoundariesIssue) {
        outsideOfImage();
        return;
      }

      if (isUpdate) {
        dispatch(setAnnotationSetForUpdate(true));
      } else {
        dispatch(setInitialDistanceToRoot());
        dispatch(setShowCreateAnnotationModal(true));
      }

      return;
    }

    mlRequest.imageFileName = getState().canvas.image?.name;
    mlRequest.sequenceId = getState().annotation.sequencePath;

    let localization: any = null;

    if (validationDto.isShapeValid) {
      try {
        if (!validationDto.shouldSkipCallToLocalization) {
          dispatch(setLocalizationLoading(true));
          localization = await getLocalization(mlRequest);
          dispatch(setLocalizationLoading(false));
        }
      } catch {
        localization = null;
      }
      dispatch(setLocalizationAndValidationData(localization, validationDto, isUpdate));
    } else if (validationDto.outOfBoundariesIssue) {
      outsideOfImage();
    } else {
      validationDto.showModal = false;
      Modal.confirm({
        title: 'Confirm',
        content: 'The finding is located on the overlapping section. Do you want to continue?',
        okText: 'Yes',
        cancelText: 'No',
        onOk: async () => {
          dispatch(setLocalizationLoading(true));
          localization = await getLocalization(mlRequest);
          dispatch(setLocalizationLoading(false));
          validationDto.overlappingIssue = false;
          validationDto.isShapeValid = !validationDto.outOfBoundariesIssue;
          dispatch(setLocalizationAndValidationData(localization, validationDto, isUpdate));
        },
        onCancel: () => {
          checkForDirtyFinding();
          dispatch(setLocalizationAndValidationData(localization, validationDto, isUpdate));
          dispatch(annotationCreationCanceled(true));
          dispatch(setSelectedAnnotationForEdit(null));
        }
      });
    }
  };

const getLocalization = async (mlRequest: MLRequest) => {
  const response = await axios.post('Integration/findingLocalization', mlRequest);
  return response.data;
};

export const setLocalizationAndValidationData =
  (localization: any, validationDto: ValidationModalDto, isUpdate: boolean): AppThunk =>
  async dispatch => {
    let showCreateAnnotationModal = true;

    if (localization !== null) {
      validationDto.localizationMissingIssue = false;
      showCreateAnnotationModal = !isUpdate;
      validationDto.showModal = false;
    } else {
      validationDto.localizationMissingIssue = true;
      showCreateAnnotationModal = false;
      validationDto.showModal = false;

      if (validationDto.shouldSkipCallToLocalization) {
        showCreateAnnotationModal = true;
      }
    }

    dispatch(setLocalizationDto(localization));
    if (showCreateAnnotationModal) {
      dispatch(setInitialDistanceToRoot());
      dispatch(setShowCreateAnnotationModal(true));
    }
    dispatch(setValidationData(validationDto));

    if (isUpdate && validationDto.isShapeValid) {
      dispatch(setAnnotationSetForUpdate(true));
    }
  };

export const getImageAnnotations =
  (isPhotocard: boolean, updatePastAnnotations: boolean = false): AppThunk =>
  async (dispatch, getState) => {
    const { selectedSequenceInfo, presentAnnotations, pathContext } = getState().annotation;
    if (selectedSequenceInfo) {
      const image = selectedSequenceInfo.images.find(
        x =>
          getImageNameWithoutExtensions(x.filename) === getImageNameWithoutExtensions(getState().annotation.selectedName) &&
          x.isPhotocard === isPhotocard
      );

      if (image && pathContext === 'with-images') {
        dispatch(setGrid(image.annotations));

        if (updatePastAnnotations || !presentAnnotations) {
          dispatch(updatedHistory(image.annotations));
        }
      }

      dispatch(checkAnnotationSettings(getState().annotation.selectedName, isPhotocard));
    }
  };

export const setDirtyFlagOnFinding =
  (updatedFindingId: string): AppThunk =>
  async (dispatch, getState) => {
    const { annotationsGrid } = getState().annotation;

    if (annotationsGrid) {
      const updatedFinding = annotationsGrid.find(x => x.id === updatedFindingId);

      if (updatedFinding) {
        const annotationGridUpdated = annotationsGrid.map(x => {
          if (x.id === updatedFindingId) {
            return {
              ...x,
              dirty: true
            };
          }
          return x;
        });

        dispatch(updatedPresentAnnotations(annotationGridUpdated));
      }
    }
  };

export const clearDirtyFlagOnFinding =
  (updatedFindingId: string): AppThunk =>
  async (dispatch, getState) => {
    const { annotationsGrid } = getState().annotation;

    if (annotationsGrid) {
      const updatedFinding = annotationsGrid.find(x => x.id === updatedFindingId);

      if (updatedFinding) {
        if (updatedFinding.dirty) {
          return;
        }

        const annotationGridUpdated = annotationsGrid.map(x => {
          if (x.id === updatedFindingId) {
            return {
              ...x,
              dirty: false
            };
          }
          return x;
        });

        dispatch(updatedPresentAnnotations(annotationGridUpdated));
      }
    }
  };

export const undo = (): AppThunk => async (dispatch, getState) => {
  const { pastAnnotations } = getState().annotation;
  if (pastAnnotations.length) {
    const last = pastAnnotations[pastAnnotations.length - 1];
    dispatch(setGrid(last!));
    dispatch(undoFinished());
    dispatch(updateAnnotations(last!));
  }
};

export const redo = (): AppThunk => async (dispatch, getState) => {
  const { futureAnnotations } = getState().annotation;
  if (futureAnnotations.length) {
    const first = futureAnnotations[0];
    dispatch(setGrid(first!));
    dispatch(redoFinished());
    dispatch(updateAnnotations(first!));
  }
};

export const updateAnnotations =
  (annotations: AnnotationsForGrid[]): AppThunk =>
  async (dispatch, getState) => {
    const { sequencePath, selectedName } = getState().annotation;
    const url = `annotations/updateimageannotations`;
    const model = {
      sequencePathId: sequencePath,
      imageFileName: selectedName,
      annotations: annotations.filter(x => x.imageName === selectedName)
    };
    const response = await axios.post<boolean>(url, model);
    dispatch(getSequenceAnnotations(true));
    ToastAnt('Updated');
  };

export const getAnnotationStandardDetails =
  (id: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      const url = `AnnotationStandards/${id}/true`;
      const response = await axios.get<AnnotationStandardDetails>(url);
      dispatch(setAnnotationStandard(response.data));
    } catch (error) {
      ToastAnt('Your user group is not authorized to access selected annotation standard');
      dispatch(redirectToRoot());
    }
  };

export const resetAllAnnotationManagerStates = (): AppThunk => async dispatch => {
  dispatch(resetState());
  dispatch(resetCanvas());
  dispatch(resetStitchedView());
  dispatch(resetFromSlice());
};

export const setupAnnotationManager =
  (turbinePath: string, containsSequence: boolean): AppThunk =>
  dispatch => {
    // dispatch(resetAllAnnotationManagerStates());
    dispatch(checkTurbineInfo(turbinePath, true, containsSequence));
    dispatch(getTurbineDataQCAssessment(turbinePath));
  };

export const checkAnnotationSettings =
  (name: string, isPhotocard: boolean): AppThunk =>
  async (dispatch, getState) => {
    const { selectedSequenceInfo } = getState().annotation;

    const imageInfo = selectedSequenceInfo!.images.find(
      x => getImageNameWithoutExtensions(x.filename) === getImageNameWithoutExtensions(name) && x.isPhotocard === isPhotocard
    )!;

    if (!imageInfo || !selectedSequenceInfo) {
      return;
    }

    dispatch(setAnnotationSettings(getAnnotationSettings(imageInfo, selectedSequenceInfo)));
  };

export const saveImageSettings =
  (contrast: number, brightness: number, saturation: number, applyToAll: boolean): AppThunk =>
  async (dispatch, getState) => {
    const { sequencePath, selectedName, selectedSequenceInfo } = getState().annotation;

    let fileNames = [selectedName];

    if (applyToAll && selectedSequenceInfo) {
      fileNames = selectedSequenceInfo.images.map(x => x.filename);
    }

    const imageSettings: ImageSettings = {
      contrast,
      brightness,
      saturation,
      applyToAll
    };

    const request: SaveImageSettings = {
      imageSettings,
      fileNames
    };

    await axios.post<ImageSettings>(`images/settings/${sequencePath}`, request);

    dispatch(setImageSetings(imageSettings));

    ToastAnt('Filters stored.');
  };

export const getImageSettings = (): AppThunk => async (dispatch, getState) => {
  const { sequencePath, selectedName } = getState().annotation;

  if (!selectedName) {
    return;
  }

  const { data: settings } = await axios.get<ImageSettings>(`images/settings/${sequencePath}/${selectedName}`);

  dispatch(setImageSetings(settings));
};

function populateRankIfNotSet(sequence: SequenceInfo, images: SequenceImage[]) {
  if (sequence.images.filter(x => x.stackingRank === 0).length > 1) {
    var sorted = images.sort(sortImagesAscByTopFunction);

    sorted.forEach((image, index) => {
      image.stackingRank = index;
    });
  }
}

export const publishSingleTurbine =
  (request: PublishRequest): AppThunk =>
  async (dispatch, getState) => {
    const { size } = getState().landing;

    dispatch(setIsPublishing(true));

    const response = await axios
      .post<boolean>('turbine/conditionalPublish', request)
      .then(response => {
        ToastAnt('Turbine sequences are successfully published');
        dispatch(checkTurbineInfo(request.selectedTurbinePaths[0]));
      })
      .catch(err => {
        if (err && err.response) {
          const statusCode = err.response.status;
          if (statusCode === 400) {
            ToastAnt(err.response.data);
            return;
          }
        }
        ToastAnt('An error occured.');
      })
      .finally(() => {
        dispatch(setIsPublishing(false));
      });

    if (getState().annotation.sequencePath.length) {
      dispatch(getSequenceAnnotations(true));
    }
  };
